import React, { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { Alert, Box, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material'
import api, { request, parseResponse } from 'utils/api'
import AccountPage from '../../components/AccountPage'

const time = (value) => value ? `${value.replace('T', ' ')} UTC` : 'Not specified'
const safeUrl = (value) => {
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null
  } catch (_) { return null }
}

const Field = ({ label, children }) => children ? (
  <Box>
    <Typography component="h2" variant="subtitle2" color="text.secondary">{label}</Typography>
    <Typography component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{children}</Typography>
  </Box>
) : null

export const SnapshotImage = ({ claimId, slot, media, alt, endpoint }) => {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    if (media?.status !== 'SAVED') return undefined
    let cancelled = false
    let objectUrl
    const controller = new AbortController()
    setUrl(null)
    setFailed(false)
    request(endpoint || `/api/user/claims/${claimId}/snapshot/media/${slot}`, { signal: controller.signal }, async response => {
      if (!response.ok) await parseResponse(response)
      return response.blob()
    }).then(blob => {
      if (cancelled) return
      objectUrl = URL.createObjectURL(blob)
      setUrl(objectUrl)
    }).catch(() => { if (!cancelled) setFailed(true) })
    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [claimId, slot, media?.status, attempt, endpoint])
  if (!media || media.status === 'ABSENT') return null
  if (media.status !== 'SAVED') return <Alert severity="info">{alt} was not saved with this claim.</Alert>
  if (failed) return <Alert severity="warning" action={<Button onClick={() => setAttempt(value => value + 1)}>Try again</Button>}>{alt} could not be loaded.</Alert>
  return url ? <Box component="img" src={url} alt={alt} onError={() => setFailed(true)}
    sx={{ maxWidth: '100%', maxHeight: slot === 'logo' ? 80 : 360, objectFit: 'contain', alignSelf: 'flex-start' }} />
    : <CircularProgress size={24} aria-label={`Loading ${alt.toLowerCase()}`} />
}

const ClaimSnapshotPage = () => {
  const { claimId } = useParams()
  const route = `/user/claims/${claimId}/snapshot`
  const { data, error, mutate } = useSWR(route, () => api.get(route), {
    shouldRetryOnError: false, revalidateOnFocus: false,
  })
  const offer = data?.offer
  const website = safeUrl(offer?.siteUrl)
  const businessWebsite = safeUrl(offer?.business?.siteUrl)
  return (
    <AccountPage title="Offer snapshot">
      <Stack spacing={2} sx={{ px: { xs: 2, md: 3 }, pb: 4, maxWidth: 960 }}>
        <Button component={RouterLink} to="/account/claimed" sx={{ alignSelf: 'flex-start' }}>Back to claimed offers</Button>
        {error ? <Alert severity="error" action={<Button onClick={() => mutate()}>Try again</Button>}>
          {error.status === 404 ? 'This claim could not be found.' : 'The snapshot could not be loaded.'}
        </Alert> : !data ? <CircularProgress aria-label="Loading snapshot" /> : <>
          <Typography color="text.secondary">Claimed {time(data.claimedAt)} · Claim {data.claimId}</Typography>
          {!data.snapshotAvailable ? <Alert severity="info">No snapshot available for this claim. Offer details were not saved when you claimed.</Alert> : <>
            <Alert severity="info">These offer details were saved when you claimed. The offer may have changed since then.</Alert>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <SnapshotImage key={`${claimId}-logo`} claimId={claimId} slot="logo" media={offer.logo} alt="Business logo" />
                <Field label="Business">{offer.business?.name}</Field>
                <SnapshotImage key={`${claimId}-image`} claimId={claimId} slot="image" media={offer.image} alt="Offer image" />
                <Field label="Headline">{offer.headline1}</Field>
                <Field label="Additional headline">{offer.headline2}</Field>
                <Field label="Description">{offer.description}</Field>
                <Field label="Terms & conditions">{offer.termsAndConditions || 'No terms and conditions were provided.'}</Field>
                <Field label="Valid until">{time(offer.endDate)}</Field>
                <Field label="Promo code">{offer.promoCode}</Field>
                {['barcode', 'qrCode'].map(kind => offer[kind] && <Field key={kind} label={kind === 'barcode' ? 'Barcode' : 'QR code'}>
                  <Box component="img" alt={kind === 'barcode' ? 'Barcode' : 'QR code'} sx={{ maxWidth: '100%', maxHeight: 300 }}
                    src={`/api/barcodes/${kind === 'barcode' ? 'barcode' : 'qrcode'}?code=${encodeURIComponent(offer[kind])}&width=400&height=${kind === 'barcode' ? 100 : 400}`} />
                  <Typography sx={{ overflowWrap: 'anywhere' }}>{offer[kind]}</Typography>
                </Field>)}
                <Field label="Offer website">{website ? <Link href={website} target="_blank" rel="noopener noreferrer">{offer.siteUrl}</Link> : offer.siteUrl}</Field>
                <Field label="Business website">{businessWebsite ? <Link href={businessWebsite} target="_blank" rel="noopener noreferrer">{offer.business.siteUrl}</Link> : offer.business?.siteUrl}</Field>
                {offer.location?.local?.map((location, index) => <Box key={index}>
                  <Field label={`Location ${index + 1}`}>{location.showAddress ? location.address : 'Address not publicly displayed'}</Field>
                  <Field label="Phone">{location.showPhone && location.phone}</Field>
                </Box>)}
              </Stack>
            </Paper>
          </>}
        </>}
      </Stack>
    </AccountPage>
  )
}

export default ClaimSnapshotPage
